import type { ComponentType, SVGProps } from 'react';

import ApplePayIcon from './images/apple-pay.svg?react';
import banknorwegianImage from './images/banknorwegian.svg';
import GooglePayIcon from './images/google-pay.svg?react';
import instabankImage from './images/instabank.svg';
import morrowbankImage from './images/morrowbank.svg';
import tfbanknordicImage from './images/tfbanknordic.svg';

export type CreditCardFeature = {
  label: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

export const features = {
  applePay: { label: 'Apple Pay', Icon: ApplePayIcon },
  googlePay: { label: 'Google Pay', Icon: GooglePayIcon },
  samsungPay: { label: 'Samsung Pay' },
  garminPay: { label: 'Garmin Pay' },
  vipps: { label: 'Vipps' },
  virtualCard: { label: 'Virtuelt kort' },
  feeFreeWithdrawals: { label: 'Gebyrfrie uttak' },
  tapWithVipps: { label: 'Tæpp med Vipps' },
} as const satisfies Record<string, CreditCardFeature>;

export type CreditCardInfo = {
  name: string;
  imageUrl: string;
  effectiveRate: number;
  creditLimit: number;
  /** days */
  interestFreePeriod: number;
  annualFee: number;
  insurances: string[];
  bonus: string;
  other: CreditCardFeature[];
  representativeExample: string;
};

/** Tiny helper that helps with type inference */
const config = (config: CreditCardInfo) => config;

export const creditCards = {
  InstaBank: config({
    name: 'Instabank',
    imageUrl: instabankImage,
    effectiveRate: 2285,
    creditLimit: 100_000_00,
    interestFreePeriod: 0,
    annualFee: 0,
    insurances: ['Kjøpsforsikring'],
    bonus: 'Roterende månedsrabatt (3-5 %) og Grønne rabatter (5 %)',
    other: [features.applePay, features.googlePay, features.samsungPay, features.virtualCard],
    representativeExample:
      'Eff. rente 22,85\u00A0%, kr 45.000\u00A0/12 mnd. Kostnad: 5.219\u00A0kr. Totalt: 50.219\u00A0kr.',
  }),
  'Morrow Bank': config({
    name: 'Morrow bank',
    imageUrl: morrowbankImage,
    effectiveRate: 2877,
    creditLimit: 100_000_00,
    interestFreePeriod: 50,
    annualFee: 0,
    insurances: ['Reiseforsikring', 'Kjøpsforsikring', 'ID-Tyveriforsikring'],
    bonus: 'Rabatter med More Experience',
    other: [
      features.applePay,
      features.googlePay,
      features.samsungPay,
      features.garminPay,
      features.vipps,
      features.feeFreeWithdrawals,
    ],
    representativeExample:
      'Eff. rente 28,77\u00A0%, kr 12.000\u00A0/12 mnd. Kostnad: 1.726\u00A0kr. Totalt: 13.726\u00A0kr.',
  }),
  'Bank Norwegian': config({
    name: 'Bank Norwegian',
    imageUrl: banknorwegianImage,
    effectiveRate: 2440,
    creditLimit: 150_000_00,
    interestFreePeriod: 45,
    annualFee: 0,
    insurances: ['Reiseforsikring', 'Kjøpsforsikring', 'ID-Tyveriforsikring'],
    bonus: 'CashPoints eller cashback (0,5-5%)',
    other: [
      features.applePay,
      features.googlePay,
      features.tapWithVipps,
      features.samsungPay,
      features.feeFreeWithdrawals,
    ],
    representativeExample:
      'Eff. rente 24,4\u00A0%, kr 15.000\u00A0/12 mnd. Kostnad: 1.849\u00A0kr. Totalt: 16.849\u00A0kr.',
  }),
  'TF Bank Nordic': config({
    name: 'TF Bank Mastercard',
    imageUrl: tfbanknordicImage,
    effectiveRate: 2669,
    creditLimit: 200_000_00,
    interestFreePeriod: 43,
    annualFee: 0,
    insurances: [
      'Reiseforsikring',
      'Kjøpsforsikring',
      'ID-Tyveriforsikring',
      'Avbestillingsforsikring',
    ],
    bonus: 'DealPass, opptil 30% rabatt',
    other: [features.applePay, features.googlePay, features.feeFreeWithdrawals],
    representativeExample:
      'Eff. rente 26,69\u00A0%, kr 25.000\u00A0/12 mnd. Kostnad: 3.337\u00A0kr. Totalt: 28.337\u00A0kr.',
  }),
} as const satisfies Record<string, CreditCardInfo>;
